import { useCallback, useEffect, useRef, useState } from 'react';
import { AppState, type AppStateStatus } from 'react-native';
import { isOnline } from '../../shell/connectivity/connectivityInfra';
import { createMercadoPagoQrForSale, fetchMercadoPagoQrPaymentAmount, fetchMpQrPaymentStatus, getSaleQr, rpcToSaleQrSnapshot, type SaleQrResponse } from '../infra/mercadopagoQrService';
import { mpQrGenerateErrorMessage } from '../domain/mpQrErrorMessage';
import {
  deriveMpQrPhase,
  MP_QR_AUTO_CLOSE_MS,
  MP_QR_HEAVY_POLL_EVERY,
  mpQrCountdownSeconds,
  mpQrPollIntervalMs,
  type MpQrPaymentPhase,
} from '../domain/mpQrPaymentPhase';
import { resolveFiscalInvoiceLabel } from '../domain/mpQrFiscalLabel';
import { addSentryBreadcrumb } from '../../shared/sentry/sentryUser';

const MAX_TRACKING_MS = 16 * 60 * 1000;

const TERMINAL_PHASES = new Set<MpQrPaymentPhase>(['approved', 'voided', 'expired', 'rejected']);

function isMpQrTerminalPhase(phase: MpQrPaymentPhase): boolean {
  return TERMINAL_PHASES.has(phase);
}

export type UseMercadoPagoQrSessionOptions = {
  saleId: string | null;
  visible: boolean;
  amount?: number | null;
  saleRef?: string | null;
  fiscalMode?: string | null;
  fiscalStatus?: string | null;
  invoiceCae?: string | null;
  regenerateNonce?: number;
  onApproved?: () => void;
  onTerminal?: () => void;
};

export type MercadoPagoQrSessionState = {
  phase: MpQrPaymentPhase;
  loading: boolean;
  error: string | null;
  qrData: string | null;
  qrUrl: string | null;
  amount: number | null;
  expiresAt: string | null;
  countdownSeconds: number | null;
  fiscalInvoiceLabel: string | null;
  autoCloseProgress: number | null;
  regenerateQr: () => void;
};

export function useMercadoPagoQrSession(options: UseMercadoPagoQrSessionOptions): MercadoPagoQrSessionState {
  const {
    saleId,
    visible,
    amount: amountProp,
    fiscalMode,
    fiscalStatus,
    invoiceCae,
    regenerateNonce = 0,
    onApproved,
    onTerminal,
  } = options;

  const [phase, setPhase] = useState<MpQrPaymentPhase>('idle');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [qrData, setQrData] = useState<string | null>(null);
  const [qrUrl, setQrUrl] = useState<string | null>(null);
  const [amount, setAmount] = useState<number | null>(amountProp ?? null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [countdownSeconds, setCountdownSeconds] = useState<number | null>(null);
  const [autoCloseProgress, setAutoCloseProgress] = useState<number | null>(null);
  const [localRegenerateNonce, setLocalRegenerateNonce] = useState(0);

  const trackingStartedAt = useRef<number | null>(null);
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoCloseTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const cancelled = useRef(false);
  const appActive = useRef(true);
  const phaseRef = useRef<MpQrPaymentPhase>('idle');
  const pollCountRef = useRef(0);
  const onApprovedRef = useRef(onApproved);
  const onTerminalRef = useRef(onTerminal);

  onApprovedRef.current = onApproved;
  onTerminalRef.current = onTerminal;

  const fiscalInvoiceLabel = resolveFiscalInvoiceLabel({
    fiscalMode,
    fiscalStatus,
    invoiceCae,
  });

  const clearTimers = useCallback(() => {
    if (pollTimer.current) {
      clearTimeout(pollTimer.current);
      pollTimer.current = null;
    }
    if (countdownTimer.current) {
      clearInterval(countdownTimer.current);
      countdownTimer.current = null;
    }
    if (autoCloseTimer.current) {
      clearInterval(autoCloseTimer.current);
      autoCloseTimer.current = null;
    }
  }, []);

  const applySnapshot = useCallback(
    async (snap: { salePaymentStatus?: string | null; salePaymentRowStatus?: string | null; paymentReconciliationStatus?: string | null; expiresAt?: string | null; qrPending?: boolean; saleVoided?: boolean }, qr?: { data: string | null; url: string | null; amount?: number | null; expires?: string | null }) => {
      if (qr?.data) setQrData(qr.data);
      if (qr?.url) setQrUrl(qr.url);
      if (qr?.amount != null) setAmount(qr.amount);
      if (qr?.expires) setExpiresAt(qr.expires);

      const prevPhase = phaseRef.current;
      const nextPhase = deriveMpQrPhase(snap);
      phaseRef.current = nextPhase;
      setPhase(nextPhase);

      if (nextPhase !== prevPhase) {
        const elapsedMs = trackingStartedAt.current != null ? Date.now() - trackingStartedAt.current : 0;
        addSentryBreadcrumb(
          'mp_qr_phase',
          `phase=${nextPhase} prev=${prevPhase} saleId=${saleId ?? 'null'} elapsed=${elapsedMs}ms`,
          nextPhase === 'approved' ? 'info' : nextPhase === 'error' ? 'error' : 'info',
          { phase: nextPhase, prevPhase, saleId, elapsedMs },
        );
      }

      const enteredTerminal = isMpQrTerminalPhase(nextPhase) && !isMpQrTerminalPhase(prevPhase);
      if (enteredTerminal) {
        setLoading(false);
        if (nextPhase === 'approved') onApprovedRef.current?.();
        onTerminalRef.current?.();
      }

      return nextPhase;
    },
    [saleId]
  );

  const refreshStatus = useCallback(async (): Promise<MpQrPaymentPhase> => {
    if (!saleId) return 'idle';
    const res = rpcToSaleQrSnapshot(await fetchMpQrPaymentStatus(saleId));
    return applySnapshot(res, {
      data: res.qrData,
      url: res.qrUrl,
      amount: res.amount ?? undefined,
      expires: res.expiresAt ?? undefined,
    });
  }, [applySnapshot, saleId]);

  const applyQrResult = useCallback(async (res: SaleQrResponse): Promise<void> => {
    setQrData(res.qrData);
    setQrUrl(res.qrUrl);
    if (res.amount != null) setAmount(res.amount);
    if (res.expiresAt) setExpiresAt(res.expiresAt);
    trackingStartedAt.current = Date.now();
    await applySnapshot(res, {
      data: res.qrData,
      url: res.qrUrl,
      amount: res.amount,
      expires: res.expiresAt ?? undefined,
    });
    setLoading(false);
  }, [applySnapshot]);

  const startAutoCloseProgress = useCallback(() => {
    const started = Date.now();
    setAutoCloseProgress(0);
    autoCloseTimer.current = setInterval(() => {
      const elapsed = Date.now() - started;
      setAutoCloseProgress(Math.min(1, elapsed / MP_QR_AUTO_CLOSE_MS));
    }, 50);
  }, []);

  useEffect(() => {
    if (phase !== 'approved' || !visible) return;
    startAutoCloseProgress();
    return () => {
      if (autoCloseTimer.current) clearInterval(autoCloseTimer.current);
      setAutoCloseProgress(null);
    };
  }, [phase, visible, startAutoCloseProgress]);

  useEffect(() => {
    if (!visible || !expiresAt) {
      setCountdownSeconds(null);
      return;
    }
    const tick = () => setCountdownSeconds(mpQrCountdownSeconds(expiresAt));
    tick();
    countdownTimer.current = setInterval(tick, 1000);
    return () => {
      if (countdownTimer.current) clearInterval(countdownTimer.current);
    };
  }, [visible, expiresAt]);

  useEffect(() => {
    const sub = AppState.addEventListener('change', (state: AppStateStatus) => {
      appActive.current = state === 'active';
      if (state === 'active' && visible && saleId && phase === 'waiting_scan') {
        void refreshStatus();
      }
    });
    return () => sub.remove();
  }, [phase, refreshStatus, saleId, visible]);

  const runGeneration = useCallback(
    async (regenerate: boolean) => {
      if (!saleId || cancelled.current) return;

      setLoading(true);
      setError(null);
      setPhase('generating');
      if (regenerate) {
        setQrData(null);
        setQrUrl(null);
      }

      try {
        const online = await isOnline();
        if (!online) {
          setError('Mercado Pago QR necesita internet. Conectate e intentá de nuevo.');
          setPhase('error');
          setLoading(false);
          return;
        }

        if (!regenerate) {
          try {
            const res = await getSaleQr(saleId);
            if (res.saleVoided) {
              setError('Esta venta fue anulada.');
              setPhase('voided');
              setLoading(false);
              return;
            }

            if (res.qrData || res.qrUrl) {
              await applyQrResult(res);
              return;
            }
          } catch {}
        }

        if (cancelled.current) return;

        {
          const total = amountProp ?? await fetchMercadoPagoQrPaymentAmount(saleId);
          if (total == null || total <= 0) {
            throw new Error('No está el monto del pago Mercado Pago QR de esta venta.');
          }
          await createMercadoPagoQrForSale(saleId, total, { regenerate });
          const res = await getSaleQr(saleId);
          if (res.qrData || res.qrUrl) {
            await applyQrResult(res);
            return;
          }
        }

        setError(
          'No se pudo generar el QR. Revisá que la caja esté asignada en Integraciones → Mercado Pago.'
        );
        setPhase('error');
        setLoading(false);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setError(mpQrGenerateErrorMessage(msg));
        setPhase('error');
        setLoading(false);
      }
    },
    [applyQrResult, applySnapshot, saleId, amountProp]
  );

  const schedulePoll = useCallback(() => {
    if (!saleId || cancelled.current || !appActive.current) return;
    const started = trackingStartedAt.current ?? Date.now();
    const elapsed = Date.now() - started;
    if (elapsed > MAX_TRACKING_MS) {
      const prevPhase = phaseRef.current;
      phaseRef.current = 'expired';
      setPhase('expired');
      setLoading(false);
      if (!isMpQrTerminalPhase(prevPhase)) {
        onTerminalRef.current?.();
      }
      return;
    }
    const delay = mpQrPollIntervalMs(elapsed);
    pollTimer.current = setTimeout(() => {
      void (async () => {
        try {
          pollCountRef.current += 1;
          const useHeavyPoll = pollCountRef.current % MP_QR_HEAVY_POLL_EVERY === 0;
          let next: MpQrPaymentPhase;
          if (useHeavyPoll) {
            // Heavy poll: getSaleQr sincroniza con MP API (GET /v1/orders/{orderId})
            // y actualiza la DB si el webhook no llegó.
            const res = await getSaleQr(saleId);
            next = await applySnapshot(res, {
              data: res.qrData,
              url: res.qrUrl,
              amount: res.amount ?? undefined,
              expires: res.expiresAt ?? undefined,
            });
          } else {
            // Light poll: RPC liviano que solo lee la DB.
            next = await refreshStatus();
          }
          if (next === 'waiting_scan' && visible && !cancelled.current) {
            schedulePoll();
          }
        } catch {
          if (visible && !cancelled.current) schedulePoll();
        }
      })();
    }, delay);
  }, [refreshStatus, saleId, visible]);

  const schedulePollRef = useRef(schedulePoll);
  schedulePollRef.current = schedulePoll;

  useEffect(() => {
    if (!visible) {
      setLocalRegenerateNonce(0);
    }
  }, [visible]);

  const runGenerationRef = useRef(runGeneration);
  runGenerationRef.current = runGeneration;

  useEffect(() => {
    if (!visible || !saleId) {
      cancelled.current = true;
      clearTimers();
      phaseRef.current = 'idle';
      setPhase('idle');
      return;
    }

    cancelled.current = false;
    const shouldRegenerate = regenerateNonce + localRegenerateNonce > 0;
    void runGenerationRef.current(shouldRegenerate);

    return () => {
      cancelled.current = true;
      clearTimers();
    };
  }, [visible, saleId, regenerateNonce, localRegenerateNonce, clearTimers]);

  useEffect(() => {
    if (!visible || !saleId || phase !== 'waiting_scan') return;

    trackingStartedAt.current = trackingStartedAt.current ?? Date.now();
    pollCountRef.current = 0;
    schedulePollRef.current();

    return () => {
      clearTimers();
    };
  }, [clearTimers, phase, saleId, visible]);

  useEffect(() => {
    if (amountProp != null) setAmount(amountProp);
  }, [amountProp]);

  const retry = useCallback(() => {
    setLocalRegenerateNonce((n) => n + 1);
  }, []);

  return {
    phase,
    loading,
    error,
    qrData,
    qrUrl,
    amount,
    expiresAt,
    countdownSeconds,
    fiscalInvoiceLabel,
    autoCloseProgress,
    regenerateQr: retry,
  };
}
